import type {
    BuyerSearchOrderByInput,
    CreateBuyerInput,
    SearchDirection,
    UpdateBuyerAccountInput,
    UpdateBuyerInput,
} from '@/graphql/graphql';
import {
    CREATE_BUYER,
    GET_BUYER,
    GET_BUYERS,
    GET_BUYER_ACCOUNT,
    GET_BUYER_SUMMARY,
    UPDATE_BUYER,
    UPDATE_BUYER_ACCOUNT,
} from '@/graphql/queries/buyers.graphql';
import { apolloClient } from './apollo-client';

const buyersSortFieldMap: Record<string, keyof BuyerSearchOrderByInput> = {
    sku: 'sku',
    legalName: 'legalName',
    contactName: 'contactName',
    contactEmail: 'contactEmail',
    publicName: 'publicName',
};

export type FetchBuyersListArgs = {
    page: number;
    pageSize: number;
    filters?: {
        [key: string]: unknown;
    };
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
};

export const fetchBuyersList = async ({ page, pageSize, filters, sortBy, sortOrder }: FetchBuyersListArgs) => {
    const orderBy: BuyerSearchOrderByInput = {};

    if (sortBy && buyersSortFieldMap[sortBy]) {
        orderBy[buyersSortFieldMap[sortBy]] = sortOrder?.toUpperCase() as SearchDirection;
    }

    const response = await apolloClient.query({
        query: GET_BUYERS,
        variables: {
            filter: {
                contactEmail: filters?.contactEmail as string | undefined,
                contactName: filters?.contactName as string | undefined,
                sku: filters?.sku ? Number(filters.sku) : undefined,
                legalName: filters?.legalName as string | undefined,
                publicName: filters?.publicName as string | undefined,
            },
            orderBy,
            pagination: {
                limit: pageSize,
                offset: (page - 1) * pageSize,
            },
        },
    });

    return {
        data: response.data.buyers.backoffice.items,
        total: response.data.buyers.backoffice.count,
    };
};

export const updateBuyer = async (id: string, input: UpdateBuyerInput) => {
    const normalizedInput = {
        ...input,
        contactEmail: input.contactEmail?.toLocaleLowerCase(),
        billingEmail: input.billingEmail?.toLocaleLowerCase(),
    };
    const result = await apolloClient.mutate({
        mutation: UPDATE_BUYER,
        variables: { id, input: normalizedInput },
    });
    return { data: result };
};

export const updateBuyerAccount = async (input: UpdateBuyerAccountInput) => {
    const normalizedInput = {
        ...input,
        contactEmail: input.contactEmail?.toLocaleLowerCase(),
        billingEmail: input.billingEmail?.toLocaleLowerCase(),
    };
    const result = await apolloClient.mutate({
        mutation: UPDATE_BUYER_ACCOUNT,
        variables: { input: normalizedInput },
    });
    return { data: result };
};

export const createBuyer = async (input: CreateBuyerInput) => {
    const normalizedInput = {
        ...input,
        contactEmail: input.contactEmail.toLocaleLowerCase(),
        billingEmail: input.billingEmail.toLocaleLowerCase(),
    };
    const result = await apolloClient.mutate({
        mutation: CREATE_BUYER,
        variables: { input: normalizedInput },
    });
    return { data: result };
};

export const getBuyer = async (id: string, full = false) => {
    const response = await apolloClient.query({
        query: full ? GET_BUYER : GET_BUYER_SUMMARY,
        variables: { id },
    });
    return response.data.buyer.backoffice;
};

export const getBuyerAccount = async (id: string) => {
    const response = await apolloClient.query({
        query: GET_BUYER_ACCOUNT,
        variables: { id },
    });
    return response.data.buyer.account;
};
